/**
 * Which single alert the Confirm Candidates pages show, and which specialty
 * warning sits under the SPECIALTIES heading.
 *
 * The legacy confirm screen stacked up to four banners — payment, competing
 * offers, charge timeline and the specialty mismatch — down the same column, and
 * made three of them clickable. The redesign shows **one** alert, chosen here,
 * and the specialty warning moves out of the stack entirely (see
 * {@link resolveSpecialtyAlert}). Both resolvers are pure so the rules are tested
 * as rules, not inferred from a rendered page.
 */

/** The page-level alert slot's candidates, in no particular order — {@link resolveShiftAlert} ranks them. */
export type TShiftAlertKind = 'payment-invalid' | 'counter-offer' | 'competing-offers';

export interface IShiftAlertContext {
  /** The office's payment method is missing or expired. */
  isPaymentInvalid: boolean;
  /**
   * The selected candidate countered the shift's hours or rate, and the page has
   * a Counter Adjustment panel to show for it. False on the multi-candidate list,
   * where a counter is a per-row chip rather than a page-level alert.
   */
  hasCounterOffer: boolean;
  /** Overlapping live offers the selected candidate holds. Undefined on the multi-candidate list. */
  competingOffersCount?: number;
}

/**
 * The one alert to render, or `null` for none.
 *
 * Ranked by how much the office needs it *before* it can act:
 *
 * 1. `payment-invalid` — confirming will fail without a card, so nothing else
 *    matters until it is fixed.
 * 2. `counter-offer` — the terms on the table are not the terms the office
 *    posted. Ranking it here is what enforces the ticket's rule that a counter
 *    offer suppresses "Confirm now to secure coverage": that alert pushes for
 *    speed, and an office being asked to accept changed hours or pay should read
 *    them, not hurry.
 * 3. `competing-offers` — time pressure on a decision the office can still make.
 *
 * The legacy screen also carried the two "Book Now, Pay Later" charge-timeline
 * banners here. That offering no longer exists, so they are not carried over —
 * an invoiced office simply sees no charge alert.
 *
 * Returning one value — never a list — is the mechanism behind "alerts must not
 * stack": there is no code path that can render two.
 */
export const resolveShiftAlert = ({
  isPaymentInvalid,
  hasCounterOffer,
  competingOffersCount = 0,
}: IShiftAlertContext): TShiftAlertKind | null => {
  if (isPaymentInvalid) {
    return 'payment-invalid';
  }

  if (hasCounterOffer) {
    return 'counter-offer';
  }

  if (competingOffersCount > 0) {
    return 'competing-offers';
  }

  return null;
};

/**
 * Which qualification warning the candidate carries — the mismatch the legacy
 * screen put in a clickable banner at the top of the page.
 *
 * The redesign moves it under the SPECIALTIES heading, beside the chips it is
 * actually about, which is also why it is resolved separately from
 * {@link resolveShiftAlert}: it is not competing for the page-level slot and
 * must still appear when that slot is taken.
 *
 * `insurance` is dentist-only — the office/professional insurance overlap is
 * only asked about for associate dentists.
 */
export type TSpecialtyAlertKind = 'specialty' | 'insurance' | 'both';

export const resolveSpecialtyAlert = ({
  specialtyMatch,
  insuranceMismatch,
}: {
  /** The candidate holds the shift's preferred specialty. */
  specialtyMatch: boolean;
  /** The candidate shares no insurance provider with the office. Dentists only. */
  insuranceMismatch: boolean;
}): TSpecialtyAlertKind | null => {
  if (!specialtyMatch && insuranceMismatch) {
    return 'both';
  }

  if (!specialtyMatch) {
    return 'specialty';
  }

  if (insuranceMismatch) {
    return 'insurance';
  }

  return null;
};
